"use client";
import { useState } from "react";
import GlowEffect from "../common/GlowEffect";
import { useServicesData } from "../../hooks/useServicesData";
import Modal from "../common/Modal";
import { SignupForm } from "../common/SignupForm";

export default function IdeasSection() {
  const { data } = useServicesData();
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const testimonials = data.ideasSection.testimonials;
  const cardPositions = data.ideasSection.cardPositions;

  interface TestimonialCardProps {
    testimonial: {
      text: string;
      author: string;
      role: string;
    };
    className?: string;
  }

  interface CallToActionProps {
    className?: string;
  }

  const TestimonialCard = ({
    testimonial,
    className = "",
  }: TestimonialCardProps) => (
    <div
      className={`bg-white/5 text-[#d9d9d9] backdrop-blur-sm border border-white/30 rounded-xl p-6 transition-all duration-300 ${className}`}
    >
      <p className="leading-relaxed mb-2">{testimonial.text}</p>
      <div>
        <p className="font-bold">{testimonial.author}</p>
        <p className="font-light">{testimonial.role}</p>
      </div>
    </div>
  );

  const CallToAction = ({ className = "" }: CallToActionProps) => (
    <div className={className}>
      <h3 className="text-4xl md:text-6xl font-medium text-white mb-8 max-w-md leading-tight">
        {data.ideasSection.ctaTitle}
      </h3>
      <button
        onClick={() => setIsSignupModalOpen(true)}
        className="bg-white text-black px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
      >
        {data.ideasSection.ctaButtonText}
      </button>
    </div>
  );

  return (
    <>
      <GlowEffect />
      <div className="py-10 md:py-20 lg:mb-60 px-4 md:px-16 relative">
        <div className="container mx-auto px-4 relative">
          <div className="text-left mb-16">
            <h2 className="text-4xl md:text-6xl font-medium text-white mb-2">
              {data.ideasSection.title}
            </h2>
            <p className="text-gray-300 text-xl">
              {data.ideasSection.subtitle}
            </p>
          </div>

          {/* Desktop: Absolutely positioned cards with central content */}
          <div className="hidden lg:block relative h-[700px] max-w-7xl mx-auto">
            {/* Testimonial Cards */}
            {testimonials.map((testimonial, index) => {
              const position = cardPositions[index];
              if (!position) return null;

              return (
                <div
                  key={index}
                  className="absolute my-10"
                  style={{
                    top: position.top,
                    right: position.right,
                    width: position.width,
                    zIndex: position.zIndex,
                  }}
                >
                  <TestimonialCard testimonial={testimonial} />
                </div>
              );
            })}

            {/* Central Content */}
            <div className="absolute top-1/2 -right-60 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <CallToAction />
            </div>
          </div>

          {/* Tablet: Grid layout */}
          <div className="hidden md:block lg:hidden">
            <CallToAction className="grid justify-center text-center mb-12" />
            <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} testimonial={testimonial} />
              ))}
            </div>
          </div>

          {/* Mobile: Stacked cards */}
          <div className="md:hidden">
            <CallToAction className="grid justify-center text-center mb-12" />
            <div className="space-y-6 max-w-md mx-auto">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Signup Modal */}
      <Modal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        title="Contact our Team"
      >
        <SignupForm />
      </Modal>
    </>
  );
}
